import { casOpsApi, CollectionModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';

export interface GeofenceAreaModel extends ModelWithId {
  name: string; // Max 128 chars with regex "^[a-zA-Z0-9-_\\.\\s]+$"
  desc: string; // description. Max 255 chars
  used: boolean; // disable delete
  fixed: boolean; // cannot rename
}

export interface CreateGeofenceAreaModel extends Omit<GeofenceAreaModel, 'id' | 'used' | 'fixed'> {}

export interface EditGeofenceAreaModel extends Omit<GeofenceAreaModel, 'id' | 'used' | 'fixed'> {}

export interface GeofenceAreaFormModel extends CreateGeofenceAreaModel, EditGeofenceAreaModel {}

export const geofenceAreasApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listGeofenceAreas: build.query<GeofenceAreaModel[], void>({
      query: () => 'api/geofencesettings/areas',
      transformResponse: (response: CollectionModel<GeofenceAreaModel>, meta, arg) =>
        response.items,
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.GeofenceArea, id: TagList }] : [],
    }),
    getGeofenceArea: build.query<GeofenceAreaModel, ModelWithId>({
      query: ({ id }) => `api/geofencesettings/areas/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.GeofenceArea, id: id }] : [],
    }),
    createGeofenceArea: build.mutation<void, CreateGeofenceAreaModel>({
      query: body => ({
        url: 'api/geofencesettings/areas',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: QueryTag.GeofenceArea, id: TagList }, QueryTag.GeofenceVersion],
    }),
    editGeofenceArea: build.mutation<void, EditGeofenceAreaModel & ModelWithId>({
      query: ({ id, ...body }) => ({
        url: `api/geofencesettings/areas/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.GeofenceArea, id: TagList },
              { type: QueryTag.GeofenceArea, id: id },
              QueryTag.GeofenceVersion,
            ]
          : [],
    }),
    deleteGeofenceArea: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/geofencesettings/areas/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.GeofenceArea, id: TagList },
              { type: QueryTag.GeofenceArea, id: id },
              QueryTag.GeofenceVersion,
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListGeofenceAreasQuery,
  useGetGeofenceAreaQuery,
  useCreateGeofenceAreaMutation,
  useEditGeofenceAreaMutation,
  useDeleteGeofenceAreaMutation,
} = geofenceAreasApi;
